import React, { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Bell,
    FileText,
    Globe,
    Lock,
    MapPin,
    Shield,
    User,
    X,
} from 'lucide-react';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';
import {
    updateUserProfile,
    shopAxios,
    getMarketplaceLocale,
    getFollowPreferences,
    setFollowPreferences,
    checkUsernameAvailable,
    getUsernameSuggestions,
} from '../../services/api.shop';
import { UpdateUserProfileDto, UserProfileDto } from '../../types';
import { ValidationError } from '../../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import {
    canSuggestUsernames,
    getPersonNameDisallowMessage,
    getUsernameDisallowMessage,
    isDisallowedUsername,
    isUsernameTooShort,
} from '../../utils/usernameRules';
import ShopSelect from '../ShopSelect';
import UsernameSuggestionChips from '../UsernameSuggestionChips';

const norm = (v: string | null | undefined) => (v ?? '').trim();

type SettingsTab = 'personal' | 'address' | 'notifications' | 'privacy';

interface ProfileSettingsPanelProps {
    profile: UserProfileDto;
    open: boolean;
    onClose: () => void;
}

const ProfileSettingsPanel: React.FC<ProfileSettingsPanelProps> = ({ profile, open, onClose }) => {
    const { t, changeLanguage, currentLang, languages } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();

    const [tab, setTab] = useState<SettingsTab>('personal');
    const [formError, setFormError] = useState<string | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [outgrownEmails, setOutgrownEmails] = useState(true);
    const [phaseChecklistEmails, setPhaseChecklistEmails] = useState(true);
    const [followingEmails, setFollowingEmails] = useState(true);
    const [newFollowerEmails, setNewFollowerEmails] = useState(true);
    const [excludeOwnProducts, setExcludeOwnProducts] = useState(false);
    const [prefSaving, setPrefSaving] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [checkingUsername, setCheckingUsername] = useState(false);
    const [suggestionChips, setSuggestionChips] = useState<string[]>([]);
    const [rerolling, setRerolling] = useState(false);

    const [formData, setFormData] = useState<UpdateUserProfileDto>({
        userName: '',
        firstName: '',
        lastName: '',
        // profilePictureUrl commented out — no self-serve image URL (moderation / unused)
        shippingStreetAddress: '',
        shippingCity: '',
        shippingPostalCode: '',
        shippingCountry: '',
        taxId: '',
        preferredLanguage: '',
    });

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    const { data: marketplaceLocale } = useQuery({
        queryKey: ['marketplaceLocale'],
        queryFn: getMarketplaceLocale,
        staleTime: 5 * 60 * 1000,
    });
    const marketplaceCountry = (marketplaceLocale?.defaultCountryCode || 'BE').toUpperCase();

    // On open only: start on Personal, clear banners / password draft
    useEffect(() => {
        if (!open) return;
        setTab('personal');
        setFormError(null);
        setSaveMessage(null);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    }, [open]);

    // Sync form from profile (also after save invalidation) — do not change tab or wipe success message
    useEffect(() => {
        if (!open) return;
        setFormData({
            userName: profile.userName || '',
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            // profilePictureUrl: profile.profilePictureUrl || '',
            shippingStreetAddress: profile.shippingStreetAddress || '',
            shippingCity: profile.shippingCity || '',
            shippingPostalCode: profile.shippingPostalCode || '',
            shippingCountry: marketplaceCountry,
            taxId: profile.taxId || '',
            preferredLanguage: profile.preferredLanguage || currentLang || 'en',
        });
        setExcludeOwnProducts(!!profile.excludeOwnProducts);
    }, [open, profile, currentLang, marketplaceCountry]);

    useEffect(() => {
        if (!open) return;
        shopAxios
            .get<{ outgrownRelistEmailsEnabled: boolean }>('/outgrown/preferences')
            .then((res) => setOutgrownEmails(res.data.outgrownRelistEmailsEnabled))
            .catch(() => {
                /* ignore */
            });
        shopAxios
            .get<{ phaseChecklistDigestEmailsEnabled: boolean }>('/user/phase-checklist-digest/preferences')
            .then((res) => setPhaseChecklistEmails(res.data.phaseChecklistDigestEmailsEnabled))
            .catch(() => {
                /* ignore */
            });
        getFollowPreferences()
            .then((res) => {
                setFollowingEmails(res.followingDigestEmailsEnabled);
                setNewFollowerEmails(res.newFollowerDigestEmailsEnabled !== false);
            })
            .catch(() => {
                /* ignore */
            });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!canSuggestUsernames(formData.firstName, formData.lastName)) {
            setSuggestionChips([]);
            return;
        }
        const first = (formData.firstName || '').trim();
        const timer = window.setTimeout(() => {
            getUsernameSuggestions({
                firstName: first,
                language: formData.preferredLanguage || currentLang,
                exclude: profile.userName ? [profile.userName] : undefined,
            })
                .then((data) => setSuggestionChips(data.chips ?? []))
                .catch(() => setSuggestionChips([]));
        }, 400);
        return () => window.clearTimeout(timer);
    }, [open, formData.firstName, formData.lastName, formData.preferredLanguage, profile.userName, currentLang]);

    const isPersonalDirty = useMemo(() => {
        const baselineLang = profile.preferredLanguage || currentLang || 'en';
        return (
            norm(formData.userName) !== norm(profile.userName) ||
            norm(formData.firstName) !== norm(profile.firstName) ||
            norm(formData.lastName) !== norm(profile.lastName) ||
            // norm(formData.profilePictureUrl) !== norm(profile.profilePictureUrl) ||
            norm(formData.preferredLanguage || currentLang) !== norm(baselineLang)
        );
    }, [formData, profile, currentLang]);

    const isAddressDirty = useMemo(() => {
        return (
            norm(formData.shippingStreetAddress) !== norm(profile.shippingStreetAddress) ||
            norm(formData.shippingCity) !== norm(profile.shippingCity) ||
            norm(formData.shippingPostalCode) !== norm(profile.shippingPostalCode) ||
            norm(formData.taxId) !== norm(profile.taxId)
        );
    }, [formData, profile]);

    const checkUsernameAvailability = useCallback(
        async (username: string) => {
            const next = username.trim();
            if (!next || isUsernameTooShort(next)) {
                setUsernameAvailable(null);
                return;
            }
            if (next.toLowerCase() === norm(profile.userName).toLowerCase()) {
                setUsernameAvailable(true);
                return;
            }
            if (isDisallowedUsername(next)) {
                setUsernameAvailable(false);
                return;
            }
            setCheckingUsername(true);
            try {
                const data = await checkUsernameAvailable(next);
                setUsernameAvailable(data.available);
            } catch {
                setUsernameAvailable(null);
            } finally {
                setCheckingUsername(false);
            }
        },
        [profile.userName]
    );

    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            if (formData.userName) checkUsernameAvailability(formData.userName);
            else setUsernameAvailable(null);
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.userName, checkUsernameAvailability, open]);

    const isPasswordDirty =
        !!passwordForm.currentPassword ||
        !!passwordForm.newPassword ||
        !!passwordForm.confirmNewPassword;

    const isDirty = isPersonalDirty || isAddressDirty || isPasswordDirty;

    const handleSave = async (e: FormEvent) => {
        e.preventDefault();
        if (!isDirty || saving) return;
        setFormError(null);
        setSaveMessage(null);

        if (isPasswordDirty) {
            if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
                setFormError(tr('account.profile.errors.passwordmismatch', 'Passwords do not match'));
                setTab('privacy');
                scrollToFirstErrorField();
                return;
            }
            if (!passwordForm.currentPassword) {
                setFormError(
                    tr(
                        'account.profile.errors.currentpasswordrequired',
                        'Current password is required'
                    )
                );
                setTab('privacy');
                scrollToFirstErrorField();
                return;
            }
        }

        const firstNameErr = getPersonNameDisallowMessage(
            formData.firstName,
            tr('page.register.firstNameLabel', 'First name')
        );
        if ((isPersonalDirty || isAddressDirty) && firstNameErr) {
            setFormError(firstNameErr);
            setTab('personal');
            scrollToFirstErrorField();
            return;
        }
        const lastNameErr = getPersonNameDisallowMessage(
            formData.lastName,
            tr('page.register.lastNameLabel', 'Last name')
        );
        if ((isPersonalDirty || isAddressDirty) && lastNameErr) {
            setFormError(lastNameErr);
            setTab('personal');
            scrollToFirstErrorField();
            return;
        }

        const usernameChanged =
            norm(formData.userName).toLowerCase() !== norm(profile.userName).toLowerCase();
        if (!norm(formData.userName)) {
            setFormError(
                tr(
                    'page.register.usernameRequiredClash',
                    'Please choose a username so you are not mixed up with another member.'
                )
            );
            setTab('personal');
            scrollToFirstErrorField();
            return;
        }
        if (usernameChanged && formData.userName) {
            if (isUsernameTooShort(formData.userName) || isDisallowedUsername(formData.userName)) {
                setFormError(
                    getUsernameDisallowMessage(formData.userName) ||
                        tr('account.profile.errors.username', 'This username is not allowed')
                );
                setTab('personal');
                scrollToFirstErrorField();
                return;
            }
            if (usernameAvailable === false) {
                setFormError(
                    getUsernameDisallowMessage(formData.userName) ||
                        tr('account.profile.errors.usernameTaken', 'Username is already taken')
                );
                setTab('personal');
                scrollToFirstErrorField();
                return;
            }
        }

        setSaving(true);
        try {
            if (isPersonalDirty || isAddressDirty) {
                const preferredLanguage = formData.preferredLanguage || currentLang || 'en';
                await updateUserProfile({
                    userName: formData.userName,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    // profilePictureUrl: formData.profilePictureUrl,
                    shippingStreetAddress: formData.shippingStreetAddress,
                    shippingCity: formData.shippingCity,
                    shippingPostalCode: formData.shippingPostalCode,
                    shippingCountry: marketplaceCountry,
                    taxId: formData.taxId,
                    preferredLanguage,
                });
                if (preferredLanguage !== currentLang) {
                    await changeLanguage(preferredLanguage, { persist: false });
                }
            }

            if (isPasswordDirty) {
                await updateUserProfile({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword,
                    confirmNewPassword: passwordForm.confirmNewPassword,
                });
                setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            }

            await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            await queryClient.invalidateQueries({ queryKey: ['products'] });
            await queryClient.invalidateQueries({ queryKey: ['search'] });
            await queryClient.invalidateQueries({ queryKey: ['myProducts'] });

            setSaveMessage(
                isPasswordDirty && !(isPersonalDirty || isAddressDirty)
                    ? tr('settings.password.updated', 'Password updated')
                    : tr('settings.saved', 'Settings saved')
            );
        } catch (err: unknown) {
            let apiMessage: string | undefined;
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (
                    err as {
                        response?: { data?: { message?: string; errors?: { General?: string } } };
                    }
                ).response?.data;
                apiMessage = data?.message || data?.errors?.General;
            }
            setFormError(
                apiMessage ||
                    tr('account.profile.errors.updatefailed', 'Failed to update profile')
            );
            scrollToFirstErrorField();
        } finally {
            setSaving(false);
        }
    };

    const toggleOutgrownEmails = async () => {
        const next = !outgrownEmails;
        setOutgrownEmails(next);
        setPrefSaving(true);
        try {
            await shopAxios.put('/outgrown/preferences', { outgrownRelistEmailsEnabled: next });
        } catch {
            setOutgrownEmails(!next);
        } finally {
            setPrefSaving(false);
        }
    };

    const togglePhaseChecklistEmails = async () => {
        const next = !phaseChecklistEmails;
        setPhaseChecklistEmails(next);
        setPrefSaving(true);
        try {
            await shopAxios.put('/user/phase-checklist-digest/preferences', {
                phaseChecklistDigestEmailsEnabled: next,
            });
        } catch {
            setPhaseChecklistEmails(!next);
        } finally {
            setPrefSaving(false);
        }
    };

    const toggleFollowingEmails = async () => {
        const next = !followingEmails;
        setFollowingEmails(next);
        setPrefSaving(true);
        try {
            await setFollowPreferences({
                followingDigestEmailsEnabled: next,
                newFollowerDigestEmailsEnabled: newFollowerEmails,
            });
        } catch {
            setFollowingEmails(!next);
        } finally {
            setPrefSaving(false);
        }
    };

    const toggleNewFollowerEmails = async () => {
        const next = !newFollowerEmails;
        setNewFollowerEmails(next);
        setPrefSaving(true);
        try {
            await setFollowPreferences({
                followingDigestEmailsEnabled: followingEmails,
                newFollowerDigestEmailsEnabled: next,
            });
        } catch {
            setNewFollowerEmails(!next);
        } finally {
            setPrefSaving(false);
        }
    };

    const toggleExcludeOwnProducts = async () => {
        const next = !excludeOwnProducts;
        setExcludeOwnProducts(next);
        setPrefSaving(true);
        try {
            await updateUserProfile({ excludeOwnProducts: next });
            await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            await queryClient.invalidateQueries({ queryKey: ['products'] });
            await queryClient.invalidateQueries({ queryKey: ['search'] });
            setSaveMessage(
                next
                    ? tr('settings.excludeOwn.on', 'Your own listings are hidden from home & search')
                    : tr('settings.excludeOwn.off', 'Your own listings are shown in home & search')
            );
        } catch {
            setExcludeOwnProducts(!next);
            setFormError(tr('account.profile.errors.updatefailed', 'Failed to update profile'));
        } finally {
            setPrefSaving(false);
        }
    };

    const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
        {
            id: 'personal',
            label: tr('settings.personal.title', 'Personal information'),
            icon: <User className="h-4 w-4" aria-hidden />,
        },
        {
            id: 'address',
            label: tr('account.settings.shipping', 'Shipping Address'),
            icon: <MapPin className="h-4 w-4" aria-hidden />,
        },
        {
            id: 'notifications',
            label: tr('settings.notifications.title', 'Notifications'),
            icon: <Bell className="h-4 w-4" aria-hidden />,
        },
        {
            id: 'privacy',
            label: tr('settings.privacy.title', 'Privacy & security'),
            icon: <Shield className="h-4 w-4" aria-hidden />,
        },
    ];

    const shortTabLabel = (id: SettingsTab, full: string) => {
        if (id === 'personal') return tr('settings.tab.personal', 'Personal');
        if (id === 'address') return tr('settings.tab.address', 'Address');
        if (id === 'notifications') return tr('settings.tab.notifications', 'Notifications');
        if (id === 'privacy') return tr('settings.tab.privacy', 'Privacy');
        return full;
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[10040]"
                onClose={() => {
                    // ShopSelect menus portal to document.body; treat that as inside the dialog.
                    if (document.querySelector('[data-shop-select-menu]')) return;
                    if (!saving) onClose();
                }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="flex w-full max-w-3xl max-h-[min(92vh,840px)] flex-col overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[var(--bg-color)] shadow-xl">
                                {/* Header */}
                                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[color-mix(in_srgb,var(--accent-color),transparent_65%)] px-4 py-3.5 sm:px-5">
                                    <Dialog.Title className="text-lg font-semibold text-[var(--text-color)]">
                                        {tr('account.profile.editsettings', 'Edit settings')}
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={saving}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)] disabled:opacity-50"
                                        aria-label={tr('common.close', 'Close')}
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                <form
                                    onSubmit={handleSave}
                                    className="flex min-h-0 flex-1 flex-col sm:flex-row"
                                >
                                    {/* Tab rail — horizontal on mobile, left on desktop */}
                                    <nav
                                        className="flex shrink-0 gap-1 overflow-x-auto border-b border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] bg-[color-mix(in_srgb,var(--bg-color),white_18%)] px-2 py-2 sm:w-48 sm:flex-col sm:overflow-x-visible sm:border-b-0 sm:border-r sm:px-2 sm:py-3"
                                        aria-label={tr('account.profile.editsettings', 'Edit settings')}
                                    >
                                        {tabs.map((item) => {
                                            const active = tab === item.id;
                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setTab(item.id);
                                                        setFormError(null);
                                                    }}
                                                    className={`flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition sm:w-full ${
                                                        active
                                                            ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_82%)] text-[var(--accent-color)] shadow-sm sm:border-l-2 sm:border-[var(--accent-color)] sm:pl-[10px]'
                                                            : 'text-[color-mix(in_srgb,var(--text-color),transparent_20%)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_90%)] sm:border-l-2 sm:border-transparent sm:pl-[10px]'
                                                    }`}
                                                >
                                                    {item.icon}
                                                    <span className="whitespace-nowrap sm:whitespace-normal">
                                                        <span className="sm:hidden">
                                                            {shortTabLabel(item.id, item.label)}
                                                        </span>
                                                        <span className="hidden sm:inline">
                                                            {shortTabLabel(item.id, item.label)}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </nav>

                                    {/* Content */}
                                    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
                                        {saveMessage && (
                                            <p className="mb-4 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_35%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_55%)] px-4 py-3 text-sm font-semibold text-[var(--text-color)]">
                                                {saveMessage}
                                            </p>
                                        )}

                                        {tab === 'personal' && (
                                            <div className="space-y-4">
                                                <Field
                                                    label={tr('page.register.firstNameLabel', 'First name')}
                                                    name="firstName"
                                                    value={formData.firstName || ''}
                                                    onChange={(v) =>
                                                        setFormData({ ...formData, firstName: v })
                                                    }
                                                />
                                                <Field
                                                    label={tr('page.register.lastNameLabel', 'Last name')}
                                                    name="lastName"
                                                    value={formData.lastName || ''}
                                                    onChange={(v) =>
                                                        setFormData({ ...formData, lastName: v })
                                                    }
                                                />
                                                <Field
                                                    label={tr('account.profile.username', 'Username')}
                                                    name="userName"
                                                    value={formData.userName || ''}
                                                    onChange={(v) => {
                                                        setFormData({ ...formData, userName: v });
                                                        setUsernameAvailable(null);
                                                    }}
                                                />
                                                {canSuggestUsernames(formData.firstName, formData.lastName) && (
                                                <UsernameSuggestionChips
                                                    chips={suggestionChips}
                                                    selected={formData.userName || ''}
                                                    onSelect={(handle) => {
                                                        setFormData({ ...formData, userName: handle });
                                                        setUsernameAvailable(true);
                                                    }}
                                                    onReroll={async () => {
                                                        if (!canSuggestUsernames(formData.firstName, formData.lastName))
                                                            return;
                                                        setRerolling(true);
                                                        try {
                                                            const data = await getUsernameSuggestions({
                                                                firstName: (formData.firstName || '').trim(),
                                                                language:
                                                                    formData.preferredLanguage || currentLang,
                                                                exclude: [
                                                                    ...suggestionChips,
                                                                    formData.userName || '',
                                                                    profile.userName || '',
                                                                ].filter(Boolean),
                                                            });
                                                            setSuggestionChips(data.chips ?? []);
                                                        } catch {
                                                            /* keep */
                                                        } finally {
                                                            setRerolling(false);
                                                        }
                                                    }}
                                                    rerolling={rerolling}
                                                />
                                                )}
                                                {formData.userName && (
                                                    <p className="text-xs">
                                                        {isUsernameTooShort(formData.userName) ? (
                                                            <span className="text-red-500">
                                                                {tr(
                                                                    'page.register.usernameTooShort',
                                                                    'Username must be at least 3 characters'
                                                                )}
                                                            </span>
                                                        ) : checkingUsername ? (
                                                            <span className="text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                                {tr(
                                                                    'page.register.usernameChecking',
                                                                    'Checking…'
                                                                )}
                                                            </span>
                                                        ) : usernameAvailable === true ? (
                                                            <span className="text-emerald-600">
                                                                {tr(
                                                                    'page.register.usernameAvailable',
                                                                    'Available'
                                                                )}
                                                            </span>
                                                        ) : usernameAvailable === false ? (
                                                            <span className="text-red-500">
                                                                {getUsernameDisallowMessage(
                                                                    formData.userName
                                                                ) ||
                                                                    tr(
                                                                        'account.profile.errors.usernameTaken',
                                                                        'Username is already taken'
                                                                    )}
                                                            </span>
                                                        ) : null}
                                                    </p>
                                                )}
                                                <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                    {tr(
                                                        'page.register.usernameHelp',
                                                        'This is how other parents see you. You can leave it or pick another.'
                                                    )}
                                                </p>

                                                {/* Profile picture URL — disabled (unused / moderation risk)
                                                <Field
                                                    label={tr('account.profile.picture', 'Profile picture URL')}
                                                    name="profilePictureUrl"
                                                    value={formData.profilePictureUrl || ''}
                                                    onChange={(v) =>
                                                        setFormData({ ...formData, profilePictureUrl: v })
                                                    }
                                                />
                                                */}

                                                <div>
                                                    <label
                                                        className="mb-1 block text-sm font-medium text-[var(--text-color)]"
                                                        htmlFor="profile-preferred-language"
                                                    >
                                                        {tr(
                                                            'settings.language.title',
                                                            'Preferred language'
                                                        )}
                                                    </label>
                                                    <ShopSelect
                                                        id="profile-preferred-language"
                                                        value={
                                                            formData.preferredLanguage ||
                                                            currentLang ||
                                                            'en'
                                                        }
                                                        onChange={(code) =>
                                                            setFormData({
                                                                ...formData,
                                                                preferredLanguage: code,
                                                            })
                                                        }
                                                        options={languages.map((lang) => ({
                                                            value: lang.code,
                                                            label: `${lang.flagEmoji ? `${lang.flagEmoji} ` : ''}${lang.nativeName || lang.name}`,
                                                        }))}
                                                        ariaLabel={tr(
                                                            'settings.language.title',
                                                            'Preferred language'
                                                        )}
                                                    />
                                                    <p className="mt-1.5 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                        <Globe className="mr-1 inline h-3 w-3" />
                                                        {tr(
                                                            'settings.language.help',
                                                            'Used for emails and the site when you are signed in.'
                                                        )}
                                                    </p>
                                                </div>

                                                {profile.email && (
                                                    <div>
                                                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                                                            {tr('account.profile.email', 'Email')}
                                                        </label>
                                                        <input
                                                            value={profile.email}
                                                            readOnly
                                                            disabled
                                                            className="w-full cursor-not-allowed rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-3 py-2.5 text-sm opacity-80"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {tab === 'address' && (
                                            <div className="space-y-4">
                                                <Field
                                                    label={tr(
                                                        'account.profile.shippingaddress.street',
                                                        'Street'
                                                    )}
                                                    name="shippingStreetAddress"
                                                    value={formData.shippingStreetAddress || ''}
                                                    onChange={(v) =>
                                                        setFormData({
                                                            ...formData,
                                                            shippingStreetAddress: v,
                                                        })
                                                    }
                                                />
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                    <Field
                                                        label={tr(
                                                            'account.profile.shippingaddress.city',
                                                            'City'
                                                        )}
                                                        name="shippingCity"
                                                        value={formData.shippingCity || ''}
                                                        onChange={(v) =>
                                                            setFormData({
                                                                ...formData,
                                                                shippingCity: v,
                                                            })
                                                        }
                                                    />
                                                    <Field
                                                        label={tr(
                                                            'account.profile.shippingaddress.postalcode',
                                                            'Postal code'
                                                        )}
                                                        name="shippingPostalCode"
                                                        value={formData.shippingPostalCode || ''}
                                                        onChange={(v) =>
                                                            setFormData({
                                                                ...formData,
                                                                shippingPostalCode: v,
                                                            })
                                                        }
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                                                        {tr(
                                                            'account.profile.shippingaddress.country',
                                                            'Country'
                                                        )}
                                                    </label>
                                                    <input
                                                        name="shippingCountry"
                                                        value={
                                                            marketplaceCountry === 'BE'
                                                                ? 'Belgium (BE)'
                                                                : marketplaceCountry
                                                        }
                                                        readOnly
                                                        disabled
                                                        className="w-full cursor-not-allowed rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-3 py-2.5 text-sm opacity-80"
                                                    />
                                                    <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                        {tr(
                                                            'account.profile.shippingaddress.country.beonly',
                                                            'Shipping is currently limited to Belgium.'
                                                        )}
                                                    </p>
                                                </div>
                                                {profile.isFlagged && !profile.hasProvidedTaxInfo && (
                                                    <Field
                                                        label={tr(
                                                            'account.profile.taxinfo',
                                                            'Tax ID'
                                                        )}
                                                        name="taxId"
                                                        value={formData.taxId || ''}
                                                        onChange={(v) =>
                                                            setFormData({ ...formData, taxId: v })
                                                        }
                                                        required
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {tab === 'notifications' && (
                                            <div className="-mx-1 divide-y divide-[color-mix(in_srgb,var(--accent-color),transparent_75%)]">
                                                <ToggleRow
                                                    title={tr(
                                                        'settings.notifications.outgrown',
                                                        'Outgrown re-list tips'
                                                    )}
                                                    description={tr(
                                                        'settings.notifications.outgrown.sub',
                                                        'Email when a purchased item may fit a re-list'
                                                    )}
                                                    enabled={outgrownEmails}
                                                    disabled={prefSaving}
                                                    onChange={() => void toggleOutgrownEmails()}
                                                />
                                                <ToggleRow
                                                    title={tr(
                                                        'settings.notifications.phaseChecklist',
                                                        'My Petito checklist tips'
                                                    )}
                                                    description={tr(
                                                        'settings.notifications.phaseChecklist.sub',
                                                        'A quiet email when a phase has listings for items still on the checklist. Not a re-list reminder.'
                                                    )}
                                                    enabled={phaseChecklistEmails}
                                                    disabled={prefSaving}
                                                    onChange={() => void togglePhaseChecklistEmails()}
                                                />
                                                <ToggleRow
                                                    title={tr(
                                                        'follow.email.pref',
                                                        'Email me when people I follow list new items'
                                                    )}
                                                    description={tr(
                                                        'follow.email.pref.help',
                                                        'We group updates (about every 6 hours) so you are not spammed.'
                                                    )}
                                                    enabled={followingEmails}
                                                    disabled={prefSaving}
                                                    onChange={() => void toggleFollowingEmails()}
                                                />
                                                <ToggleRow
                                                    title={tr(
                                                        'follow.email.newfollowers.pref',
                                                        'Email me when I get new followers'
                                                    )}
                                                    description={tr(
                                                        'follow.email.newfollowers.pref.help',
                                                        'We group new followers (about every 6 hours) so you are not spammed.'
                                                    )}
                                                    enabled={newFollowerEmails}
                                                    disabled={prefSaving}
                                                    onChange={() => void toggleNewFollowerEmails()}
                                                />
                                            </div>
                                        )}

                                        {tab === 'privacy' && (
                                            <div className="space-y-5">
                                                <div>
                                                    <div className="mb-3 flex items-center gap-2">
                                                        <Lock className="h-4 w-4 text-[var(--text-color)]" />
                                                        <h3 className="font-semibold text-[var(--text-color)]">
                                                            {tr(
                                                                'settings.privacy.password',
                                                                'Change password'
                                                            )}
                                                        </h3>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <Field
                                                            label={tr(
                                                                'account.profile.password.current',
                                                                'Current password'
                                                            )}
                                                            name="currentPassword"
                                                            type="password"
                                                            value={passwordForm.currentPassword}
                                                            onChange={(v) =>
                                                                setPasswordForm({
                                                                    ...passwordForm,
                                                                    currentPassword: v,
                                                                })
                                                            }
                                                        />
                                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                            <Field
                                                                label={tr(
                                                                    'account.profile.password.new',
                                                                    'New password'
                                                                )}
                                                                name="newPassword"
                                                                type="password"
                                                                value={passwordForm.newPassword}
                                                                onChange={(v) =>
                                                                    setPasswordForm({
                                                                        ...passwordForm,
                                                                        newPassword: v,
                                                                    })
                                                                }
                                                            />
                                                            <Field
                                                                label={tr(
                                                                    'account.profile.password.confirm',
                                                                    'Confirm new password'
                                                                )}
                                                                name="confirmNewPassword"
                                                                type="password"
                                                                value={
                                                                    passwordForm.confirmNewPassword
                                                                }
                                                                onChange={(v) =>
                                                                    setPasswordForm({
                                                                        ...passwordForm,
                                                                        confirmNewPassword: v,
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="border-t border-[color-mix(in_srgb,var(--accent-color),transparent_75%)] pt-2">
                                                    <ToggleRow
                                                        title={tr(
                                                            'settings.excludeOwn.title',
                                                            'Exclude own products'
                                                        )}
                                                        description={tr(
                                                            'settings.excludeOwn.sub',
                                                            'Hide your listings from home and search so the first results are not always your own'
                                                        )}
                                                        enabled={excludeOwnProducts}
                                                        disabled={prefSaving}
                                                        onChange={() =>
                                                            void toggleExcludeOwnProducts()
                                                        }
                                                    />
                                                </div>

                                                <Link
                                                    to="/privacy"
                                                    onClick={onClose}
                                                    className="flex items-center justify-between rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] px-4 py-3.5 transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_90%)]"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="h-4 w-4 text-[color-mix(in_srgb,var(--text-color),transparent_40%)]" />
                                                        <div>
                                                            <p className="font-medium text-[var(--text-color)]">
                                                                {tr(
                                                                    'settings.privacy.policy',
                                                                    'Privacy policy'
                                                                )}
                                                            </p>
                                                            <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                                {tr(
                                                                    'settings.privacy.policy.sub',
                                                                    'How we process personal data on Petito'
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Link>
                                            </div>
                                        )}

                                        <ValidationError error={formError ?? undefined} />
                                    </div>
                                </form>

                                <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-[color-mix(in_srgb,var(--accent-color),transparent_65%)] bg-[color-mix(in_srgb,var(--bg-color),white_12%)] px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={saving}
                                        className="min-h-11 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] px-4 py-2.5 text-sm font-medium text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] disabled:opacity-60"
                                    >
                                        {tr('common.cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!isDirty || saving}
                                        onClick={(e) => {
                                            void handleSave(e as unknown as FormEvent);
                                        }}
                                        className="min-h-11 rounded-xl bg-[var(--accent-color)] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {saving
                                            ? tr('state.loading', 'Loading…')
                                            : tr('settings.savechanges', 'Save changes')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

function Field({
    label,
    name,
    value,
    onChange,
    type = 'text',
    required,
}: {
    label: string;
    name: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    required?: boolean;
}) {
    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor={name}>
                {label}
            </label>
            <input
                id={name}
                name={name}
                type={type}
                value={value}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="min-h-11 w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2.5 text-sm text-[var(--text-color)]"
            />
        </div>
    );
}

function ToggleRow({
    title,
    description,
    enabled,
    onChange,
    disabled,
    comingSoon,
}: {
    title: string;
    description: string;
    enabled: boolean;
    onChange?: () => void;
    disabled?: boolean;
    comingSoon?: boolean;
}) {
    return (
        <div className="flex items-center justify-between gap-4 px-1 py-4">
            <div className="min-w-0">
                <p className="font-medium text-[var(--text-color)]">
                    {title}
                    {comingSoon && (
                        <span className="ml-2 text-xs font-normal text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                            (soon)
                        </span>
                    )}
                </p>
                <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                    {description}
                </p>
            </div>
            <Switch
                checked={enabled}
                onChange={() => onChange?.()}
                disabled={disabled}
                className={`${enabled ? 'bg-[var(--accent-color)]' : 'bg-gray-200'} ${
                    disabled ? 'opacity-50' : ''
                } relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:cursor-not-allowed`}
            >
                <span
                    className={`${
                        enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition`}
                />
            </Switch>
        </div>
    );
}

export default ProfileSettingsPanel;
